// src/pages/MunicipalityUsers.jsx

import {
  ArrowLeft,
  CalendarCheck,
  Mail,
  MapPin,
  MessageSquare,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/axios";
import bgImage from "../assets/images/1768687732162_3D.png";

const serviceNames = {
  lawyer: "محامون",
  translator: "مترجمون",
  notary: "موثقون",
  judicial_expert: "خبراء قضائيون",
};

export default function MunicipalityUsers() {
  const { serviceName, baladiya } = useParams();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [chatPermissions, setChatPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const checkCanChat = async (artisanId) => {
    try {
      const { data } = await api.get(`/can-chat/${artisanId}`);
      return data.can_chat === true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError("");

        const { data } = await api.get("/users");

        if (data.status !== "success") {
          throw new Error("فشل تحميل البيانات");
        }

        const filtered = data.data.filter(
          (user) =>
            user.type_work === serviceName && user.blaidait === baladiya,
        );

        if (!mounted) return;

        setUsers(filtered);

        const permissions = {};

        await Promise.all(
          filtered.map(async (user) => {
            permissions[user.id] = await checkCanChat(user.id);
          }),
        );

        if (mounted) {
          setChatPermissions(permissions);
        }
      } catch (error) {
        console.error(error);

        if (mounted) {
          setError("حدث خطأ أثناء تحميل المستخدمين");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchUsers();

    return () => {
      mounted = false;
    };
  }, [serviceName, baladiya]);

  return (
    <div
      className="relative min-h-screen bg-cover bg-center"
      style={{
        backgroundImage: `url(${bgImage})`,
      }}
    >
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-white/90 backdrop-blur border-b shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-green-700 hover:text-green-800"
            >
              <ArrowLeft size={18} />
              رجوع
            </button>

            <h1 className="text-xl font-bold text-gray-800">
              {serviceNames[serviceName] ?? serviceName}
              {" - "}
              {baladiya}
            </h1>
          </div>
        </header>

        {/* Body */}
        <main className="max-w-6xl mx-auto px-6 py-10">
          {loading && (
            <div className="text-center py-20 text-gray-500">
              جارٍ تحميل المختصين...
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center text-red-600">
              {error}
            </div>
          )}

          {!loading && !error && users.length === 0 && (
            <div className="bg-white rounded-lg shadow border p-10 text-center">
              لا يوجد مختصون في هذه البلدية حالياً
            </div>
          )}

          {!loading && users.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {users.map((user) => {
                const canChat = chatPermissions[user.id] ?? false;

                return (
                  <div
                    key={user.id}
                    className="bg-white rounded-xl border shadow-sm hover:shadow-lg transition duration-300 p-5"
                  >
                    <div className="flex items-center gap-4 mb-5">
                      {user.profile_image ? (
                        <img
                          src={user.profile_image}
                          alt={user.name}
                          className="w-16 h-16 rounded-full object-cover border"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                          <User className="text-green-700" size={28} />
                        </div>
                      )}

                      <div className="min-w-0">
                        <h3 className="font-semibold text-lg truncate">
                          {user.name}
                        </h3>

                        <p className="text-sm text-gray-500 flex items-center gap-1 truncate">
                          <Mail size={14} />
                          {user.email}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <button
                        onClick={() =>
                          navigate(`/booking/${serviceName}/${user.id}`)
                        }
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition"
                      >
                        <CalendarCheck size={18} />
                        حجز موعد
                      </button>

                      {/* <button
                        disabled={!canChat}
                        onClick={() => {
                          if (!canChat) return;
                          navigate(`/chat/${user.id}`);
                        }}
                        className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg transition
                          ${
                            canChat
                              ? "bg-blue-600 hover:bg-blue-700 text-white"
                              : "bg-gray-200 text-gray-500 cursor-not-allowed"
                          }`}
                      >
                        <MessageSquare size={18} />
                        {canChat ? "مراسلة" : "المراسلة متاحة بعد تأكيد الموعد"}
                      </button> */}

                      {user.address_googel_map && (
                        <button
                          onClick={() =>
                            window.open(
                              user.address_googel_map,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition"
                        >
                          <MapPin size={18} />
                          عرض الموقع
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
